import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, CheckCircle, XCircle, MessageSquare, User, Clock, Coins } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import './MaintenanceReview.css';
import { BaseComponentImpl } from '../../../types/implementations';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getLifetimeForDirection } from '../TemplatesEditor/types';
import { Building } from '../../../types/protocols';
import { BaseType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import { useUserNames } from '../../../Helpers/useUserNames';
import { 
    generateYearlyPlansForBuilding, 
    YearlyActivity,
    ComponentMaintenanceData,
    MaintenancePlan,
    sortActivitiesByDate
} from '../../../Helpers/maintenanceCostUtils';
import MaintenanceActivityList from '../../../Components/MaintenanceActivityList';
import { Helmet } from 'react-helmet-async';

interface ComponentMaintenanceDataWithDone extends ComponentMaintenanceData {
    isDone?: boolean;
}

interface MaintenanceDraft extends MaintenancePlan {
    rejectionComment?: {
        comment: string;
        rejectedBy: string;
        rejectedAt: string;
    };
}

interface MaintenanceProposal extends MaintenancePlan {
    submission: {
        submittedBy: string;
        submittedAt: string;
        status: 'pending' | 'approved' | 'rejected';
        reviewedBy?: string;
        reviewedAt?: string;
        rejectionComment?: string;
    };
}

interface MaintenanceActive extends MaintenancePlan {
    // Uses the same structure as MaintenancePlan
}

// YearlyActivity interface is imported from maintenanceCostUtils

const MaintenanceReview: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const { userNames, loadAllUsers } = useUserNames();
    const [building, setBuilding] = useState<Building | null>(null);
    const [proposedPlan, setProposedPlan] = useState<MaintenanceProposal | null>(null);
    const [activePlan, setActivePlan] = useState<MaintenanceActive | null>(null);
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [maintenanceTemplates, setMaintenanceTemplates] = useState<TemplatesByType>({});
    const [componentConditions, setComponentConditions] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [yearsToShow, setYearsToShow] = useState<number>(10);
    const [proposedYearlyPlans, setProposedYearlyPlans] = useState<YearlyActivity[]>([]);
    const [activeYearlyPlans, setActiveYearlyPlans] = useState<YearlyActivity[]>([]);
    const [reviewing, setReviewing] = useState<boolean>(false);
    const [rejectionComment, setRejectionComment] = useState<string>('');

    const db = getDatabase();
    const auth = getAuth();

    // Load all required data
    const loadData = async (): Promise<void> => {
        try {
            setLoading(true);

            const [summarySnapshot, modelSnapshot, proposedSnapshot, activeSnapshot, lifetimesSnapshot, templatesSnapshot, conditionsSnapshot] = await Promise.all([
                get(ref(db, `buildingsSummary/${buildingKey!}`)),
                get(ref(db, `buildings/${buildingKey!}`)),
                get(ref(db, `maintenance/proposed/${buildingKey!}`)),
                get(ref(db, `maintenance/active/${buildingKey!}`)),
                get(ref(db, 'maintenance/componentLifetimes')),
                get(ref(db, 'maintenance/templates')),
                get(ref(db, `componentConditions/${buildingKey!}`))
            ]);

            // Load building data
            if (summarySnapshot.exists() || modelSnapshot.exists()) {
                const building: Building = {
                    key: buildingKey!,
                    summary: summarySnapshot.exists() ? summarySnapshot.val() : { key: buildingKey! },
                    model: modelSnapshot.exists() ? modelSnapshot.val() : { key: buildingKey! }
                };
                setBuilding(building);
            }

            // Load proposed plan
            if (proposedSnapshot.exists()) {
                const proposedData = proposedSnapshot.val() as MaintenanceProposal;
                setProposedPlan(proposedData);
            } else {
                setProposedPlan(null);
            }

            // Load active plan
            if (activeSnapshot.exists()) {
                const activeData = activeSnapshot.val() as MaintenanceActive;
                setActivePlan(activeData);
            } else {
                setActivePlan(null);
            }

            // Load component conditions
            if (conditionsSnapshot.exists()) {
                const data = conditionsSnapshot.val() as Record<string, any>;
                setComponentConditions(data);
            } else {
                setComponentConditions({});
            }

            // Load component lifetimes
            if (lifetimesSnapshot.exists()) {
                const data = lifetimesSnapshot.val() as Record<string, ComponentLifetime>;
                setComponentLifetimes(data);
            }

            // Load maintenance templates
            if (templatesSnapshot.exists()) {
                const data = templatesSnapshot.val() as Record<string, Record<string, any>>;
                const templatesByType: TemplatesByType = {};
                
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => ({
                            id,
                            name: template.name || '',
                            description: template.description || '',
                            interval_type: template.interval_type || 'years',
                            interval_value: template.interval_value || 1,
                            cost: template.cost ?? 0,
                            cost_unit: template.cost_unit ?? 'per_sqm',
                            directionalIntervals: template.directionalIntervals || undefined
                        }));
                    }
                });
                setMaintenanceTemplates(templatesByType);
            }

            // Load user names
            await loadAllUsers();

        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Generate yearly plans using shared utility
    const generateYearlyPlans = (plan: MaintenanceProposal | MaintenanceActive | null): YearlyActivity[] => {
        if (!building?.model?.structure?.components || !plan || !componentLifetimes || !maintenanceTemplates) {
            return [];
        }

        const buildingName = building.summary?.name || 'Namnlös byggnad';
        
        return generateYearlyPlansForBuilding({
            building,
            plan,
            componentLifetimes,
            maintenanceTemplates,
            componentConditions,
            yearsToShow,
            buildingName
        });
    };

    // Approve plan
    const handleApprovePlan = async (): Promise<void> => {
        if (!proposedPlan || !buildingKey) return;

        setReviewing(true);
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            // Archive current active plan if it exists
            if (activePlan) {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const timestamp = `${year}-${month}-${day} ${hours}:${minutes}`; // Local time YYYY-MM-DD hh:mm format
                const archiveData = {
                    ...activePlan,
                    archivedAt: now.toISOString(),
                    archivedBy: user.uid
                };
                await set(ref(db, `maintenance/archived/${buildingKey}/${timestamp}`), archiveData);
            }

            // Move proposed plan to active, filtering out disabled activities
            const filteredComponents: Record<string, any> = {};
            
            Object.entries(proposedPlan.components).forEach(([componentKey, componentData]) => {
                const filteredMaintenanceActivities: Record<string, any> = {};
                
                // Filter out disabled maintenance activities
                Object.entries(componentData.maintenanceActivities || {}).forEach(([templateId, activity]) => {
                    if (activity.disabled !== true) {
                        filteredMaintenanceActivities[templateId] = activity;
                    }
                });
                
                filteredComponents[componentKey] = {
                    ...componentData,
                    maintenanceActivities: filteredMaintenanceActivities
                };
            });
            
            const newActivePlan: MaintenanceActive = {
                components: filteredComponents,
                metadata: proposedPlan.metadata
            };
            
            // First, save to active
            await set(ref(db, `maintenance/active/${buildingKey}`), newActivePlan);
            
            // Verify the active plan was saved successfully
            const activeSnapshot = await get(ref(db, `maintenance/active/${buildingKey}`));
            if (!activeSnapshot.exists()) {
                throw new Error('Failed to save active plan - verification failed');
            }

            // Only after successful verification, remove from proposed
            await set(ref(db, `maintenance/proposed/${buildingKey}`), null);

            // Redirect to drafts page
            navigate(`/building/${buildingKey}`);

        } catch (error) {
            console.error('Error approving plan:', error);
            alert('Ett fel uppstod vid godkännande av planen. Försök igen.');
        } finally {
            setReviewing(false);
        }
    };

    // Reject plan
    const handleRejectPlan = async (comment?: string): Promise<void> => {
        const finalComment = comment || rejectionComment;
        if (!proposedPlan || !buildingKey || !finalComment.trim()) return;

        setReviewing(true);
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }

            // Move proposal back to draft with rejection comment
            const draftWithComment: MaintenanceDraft = {
                components: proposedPlan.components,
                metadata: proposedPlan.metadata,
                rejectionComment: {
                    comment: finalComment.trim(),
                    rejectedBy: user.uid,
                    rejectedAt: new Date().toISOString()
                }
            };
            
            // First, save to drafts
            await set(ref(db, `maintenance/drafts/${buildingKey}`), draftWithComment);
            
            // Verify the draft was saved successfully
            const draftSnapshot = await get(ref(db, `maintenance/drafts/${buildingKey}`));
            if (!draftSnapshot.exists()) {
                throw new Error('Failed to save draft - verification failed');
            }

            // Only after successful verification, remove from proposed
            await set(ref(db, `maintenance/proposed/${buildingKey}`), null);

            // Redirect to drafts page
            navigate(`/building/${buildingKey}`);

        } catch (error) {
            console.error('Error rejecting plan:', error);
            alert('Ett fel uppstod vid avslag av planen. Försök igen.');
        } finally {
            setReviewing(false);
        }
    };

    useEffect(() => {
        if (buildingKey) {
            loadData();
        }
    }, [buildingKey]);

    useEffect(() => {
        if (!loading) {
            setProposedYearlyPlans(generateYearlyPlans(proposedPlan));
            setActiveYearlyPlans(generateYearlyPlans(activePlan));
        }
    }, [loading, yearsToShow, proposedPlan, activePlan, componentLifetimes, maintenanceTemplates, componentConditions]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar granskning...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Kunde inte ladda byggnadsinformation för nyckel: {buildingKey!}</p>
            </div>
        );
    }

    if (!proposedPlan) {
        return (
            <div className="alert alert-info">
                <h4>Inget förslag att granska</h4>
                <p>Inga väntande förslag hittades för denna byggnad.</p>
                <button 
                    className="btn btn-primary"
                    onClick={() => navigate(`/building/${buildingKey}`)}
                >
                    <ArrowLeft size={16} className="me-2" />
                    Tillbaka till utkast
                </button>
            </div>
        );
    }

    const submitterName = userNames[proposedPlan.submission.submittedBy] || 'Okänd användare';
    const submissionDate = new Date(proposedPlan.submission.submittedAt).toLocaleDateString('sv-SE');
    const buildingName = building.summary?.name || 'Namnlös byggnad';

    const proposedTotalCost = proposedYearlyPlans.reduce((total, year) => total + year.totalCost, 0);
    const activeTotalCost = activeYearlyPlans.reduce((total, year) => total + year.totalCost, 0);
    const costDifference = proposedTotalCost - activeTotalCost;

    return (
        <div className="maintenance-review">
            <Helmet>
                <title>{`Granska underhållsplan - ${building?.summary?.name || 'Byggnad'} - Molnfastigheter`}</title>
            </Helmet>
            {/* Top Navigation Buttons */}
            <div className="top-navigation">
                <div className="back-button-container">
                    <button 
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(`/building/${buildingKey}`)}
                    >
                        <ArrowLeft size={20} className="me-2" />
                        Tillbaka till byggnad
                    </button>
                </div>
                <div className="action-buttons-container">
                    {/* Action buttons can be added here if needed */}
                </div>
            </div>

            {/* Header */}
            <div className="header-section">
                <div className="header-content" style={{marginTop: '30px', marginBottom: '30px'}}>
                    <h1 className="display-6 fw-bold text-dark">
                        Granska underhållsplan: {buildingName}
                    </h1>
                    <p className="lead text-muted">
                        Förslag från {submitterName} inlämnat den {submissionDate}
                    </p>
                </div>
            </div>

            {/* Submission Info */}
            <div className="submission-info-section">
                <div className="card" style={{padding: '20px'}}>
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <div className="d-flex align-items-center">
                                    <User size={20} className="me-2 text-primary" />
                                    <span className="fw-bold me-2">Inlämnad av:</span>
                                    <span>{submitterName}</span>
                                </div>
                                <div className="d-flex align-items-center mt-2">
                                    <Clock size={20} className="me-2 text-primary" />
                                    <span className="fw-bold me-2">Datum:</span>
                                    <span>{submissionDate}</span>
                                </div>
                            </div>
                            <div className="col-md-4 text-end">
                                <div className="d-flex gap-2 justify-content-end">
                                    <button 
                                        className="btn btn-success"
                                        onClick={handleApprovePlan}
                                        disabled={reviewing}
                                    >
                                        {reviewing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Godkänner...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={16} className="me-2" />
                                                Godkänn
                                            </>
                                        )}
                                    </button>
                                    <button 
                                        className="btn btn-danger"
                                        onClick={() => {
                                            const comment = prompt('Ange anledning till avslag:');
                                            if (comment) {
                                                handleRejectPlan(comment);
                                            }
                                        }}
                                        disabled={reviewing}
                                    >
                                        {reviewing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Avslår...
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={16} className="me-2" />
                                                Avslå
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Comparison */}
            <div className="cost-comparison-section">
                <div className="card">
                    <div className="card-header">
                        <h5 className="mb-0">
                            <Coins size={20} className="me-2" />
                            Kostnadsjämförelse ({yearsToShow} år)
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row" style={{padding: '20px'}}>
                            <div className="col-md-4">
                                <div className="text-center">
                                    <h6 className="text-muted">Nuvarande plan</h6>
                                    <h4 className="text-primary">{Math.round(activeTotalCost).toLocaleString('sv-SE')} kr</h4>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="text-center">
                                    <h6 className="text-muted">Föreslagen plan</h6>
                                    <h4 className="text-success">{Math.round(proposedTotalCost).toLocaleString('sv-SE')} kr</h4>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="text-center">
                                    <h6 className="text-muted">Skillnad</h6>
                                    <h4 className={costDifference >= 0 ? 'text-danger' : 'text-success'}>
                                        {costDifference >= 0 ? '+' : ''}{Math.round(costDifference).toLocaleString('sv-SE')} kr
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Yearly Plans Comparison */}
            <div className="yearly-plans">
                {proposedYearlyPlans.map((yearPlan, index) => {
                    const activeYearPlan = activeYearlyPlans[index];
                    const costDiff = yearPlan.totalCost - (activeYearPlan?.totalCost || 0);
                    
                    return (
                        <div key={yearPlan.year} className="year-card">
                            <div className="year-header">
                                <h3 className="year-title">{yearPlan.year}</h3>
                                <div className="year-stats">
                                    <span className="activity-count me-3">
                                        {yearPlan.activities.length} aktivitet{yearPlan.activities.length !== 1 ? 'er' : ''}
                                    </span>
                                    {yearPlan.totalCost > 0 && (
                                        <span className="year-cost">
                                            <Coins size={16} className="me-1" />
                                            {Math.round(yearPlan.totalCost).toLocaleString('sv-SE')} kr
                                        </span>
                                    )}
                                    {costDiff !== 0 && (
                                        <span className={`cost-difference ms-2 ${costDiff > 0 ? 'text-danger' : 'text-success'}`}>
                                            ({costDiff > 0 ? '+' : ''}{costDiff.toLocaleString('sv-SE')} kr)
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {yearPlan.activities.length > 0 ? (
                                <MaintenanceActivityList activities={sortActivitiesByDate(yearPlan.activities)} />
                            ) : (
                                <div className="no-activities">
                                    <span className="text-muted">Inga planerade aktiviteter</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MaintenanceReview;
